import { Link } from 'react-router';
import { useEffect, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { Shield, CurrencyDollar, Eye, Copy, Check } from '@phosphor-icons/react';

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const PIPELINE_STEPS = [
  'Auth', 'Rate', 'Anomaly', 'Policy', 'DLP',
  'Budget', 'Execute', 'DLP Out', 'Audit',
] as const;

const FEED_ENTRIES = [
  { ago: '1s',  actor: 'claude-agent-7',    tool: 'http.get',    target: 'api.github.com',     status: 'ALLOW',    ms: '2.3' },
  { ago: '3s',  actor: 'langgraph-worker',  tool: 'slack.post',  target: 'api.slack.com',      status: 'APPROVAL', ms: '--' },
  { ago: '5s',  actor: 'n8n-automation',    tool: 'http.post',   target: 'internal-api',       status: 'DENY',     ms: '0.1' },
  { ago: '8s',  actor: 'openai-agent-3',    tool: 'http.get',    target: 'api.stripe.com',     status: 'ALLOW',    ms: '4.1' },
  { ago: '11s', actor: 'claude-agent-12',   tool: 'http.delete', target: 'db.production',      status: 'DENY',     ms: '0.2' },
  { ago: '14s', actor: 'langchain-bot',     tool: 'http.post',   target: 'api.sendgrid.com',   status: 'ALLOW',    ms: '3.8' },
  { ago: '17s', actor: 'custom-agent-9',    tool: 'slack.post',  target: 'hooks.slack.com',    status: 'ALLOW',    ms: '1.9' },
  { ago: '20s', actor: 'n8n-workflow-5',    tool: 'http.put',    target: 'api.notion.com',     status: 'APPROVAL', ms: '--' },
  { ago: '23s', actor: 'claude-agent-2',    tool: 'http.get',    target: 'api.openai.com',     status: 'ALLOW',    ms: '5.2' },
  { ago: '26s', actor: 'gpt-agent-1',       tool: 'http.post',   target: 'webhook.site',       status: 'DENY',     ms: '0.1' },
];

const STATUS_COLOR: Record<string, string> = {
  ALLOW:    'text-accent-teal',
  DENY:     'text-[var(--color-error)]',
  APPROVAL: 'text-[var(--color-warning)]',
};

/* ------------------------------------------------------------------ */
/*  Pipeline Animation Component                                       */
/* ------------------------------------------------------------------ */

function PipelineVisualization() {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const requestCountRef = useRef(0);
  const animFrameRef = useRef<number>(0);
  const isVisible = useInView(containerRef, { once: false, amount: 0.3 });

  // Counters
  const [processed, setProcessed] = useState(14392);
  const [blocked, setBlocked] = useState(847);
  const [pending, setPending] = useState(3);

  // Animate counters
  useEffect(() => {
    const interval = setInterval(() => {
      setProcessed(p => p + Math.floor(Math.random() * 3) + 1);
      if (Math.random() > 0.7) setBlocked(b => b + 1);
      if (Math.random() > 0.95) setPending(p => Math.max(1, p + (Math.random() > 0.5 ? 1 : -1)));
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  // Canvas pipeline animation
  useEffect(() => {
    if (!isVisible) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;

    function resize() {
      if (!canvas || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = 180 * dpr;
      canvas.style.width = rect.width + 'px';
      canvas.style.height = '180px';
      ctx!.scale(dpr, dpr);
    }
    resize();
    window.addEventListener('resize', resize);

    const TEAL = '#06B6A4';
    const RED = '#F87171';
    const AMBER = '#FBBF24';
    const SURFACE1 = '#12161D';
    const BORDER = '#2A3040';
    const TEXT_SEC = '#8892A7';
    const TEXT_PRI = '#E8ECF4';

    interface Dot {
      progress: number;       // 0..1 along path
      speed: number;
      color: string;
      type: 'allow' | 'deny' | 'approval';
      pauseAt?: number;       // pause progress position
      pauseRemaining: number;
      dead: boolean;
      glowIntensity: number;
      denyForkProgress: number;
    }

    const dots: Dot[] = [];

    function spawnDot() {
      requestCountRef.current++;
      const count = requestCountRef.current;
      let type: Dot['type'] = 'allow';
      if (count % 5 === 0) type = 'deny';
      else if (count % 8 === 0) type = 'approval';

      dots.push({
        progress: 0,
        speed: 0.0018 + Math.random() * 0.0007,
        color: TEAL,
        type,
        pauseAt: type === 'approval' ? 0.38 : undefined,  // Policy node ~38%
        pauseRemaining: type === 'approval' ? 180 : 0,     // ~3 sec at 60fps
        dead: false,
        glowIntensity: 1,
        denyForkProgress: 0,
      });
    }

    // Spawn first dot immediately
    spawnDot();

    function getNodePositions(w: number) {
      const stepCount = PIPELINE_STEPS.length;
      const paddingX = Math.max(90, w * 0.08);
      const usable = w - paddingX * 2;
      const gap = usable / (stepCount - 1);
      return PIPELINE_STEPS.map((_, i) => ({
        x: paddingX + i * gap,
        y: 70,
      }));
    }

    function getPathPosition(progress: number, nodes: { x: number; y: number }[]) {
      const totalSegments = nodes.length - 1;
      const segment = Math.min(Math.floor(progress * totalSegments), totalSegments - 1);
      const segProgress = (progress * totalSegments) - segment;
      const from = nodes[segment];
      const to = nodes[Math.min(segment + 1, nodes.length - 1)];
      return {
        x: from.x + (to.x - from.x) * segProgress,
        y: from.y + (to.y - from.y) * segProgress,
        segment,
      };
    }

    function draw() {
      if (!canvas || !ctx) return;
      const w = canvas.width / dpr;
      const h = canvas.height / dpr;
      ctx.clearRect(0, 0, w, h);

      const nodes = getNodePositions(w);
      const nodeW = 64;
      const nodeH = 30;

      // Active node glow tracking
      const activeNodes = new Set<number>();
      dots.forEach(d => {
        if (d.dead) return;
        const pos = getPathPosition(d.progress, nodes);
        activeNodes.add(pos.segment);
        if (pos.segment > 0) activeNodes.add(pos.segment - 1);
      });

      // Draw connections
      for (let i = 0; i < nodes.length - 1; i++) {
        ctx.beginPath();
        ctx.moveTo(nodes[i].x + nodeW / 2, nodes[i].y);
        ctx.lineTo(nodes[i + 1].x - nodeW / 2, nodes[i + 1].y);
        ctx.strokeStyle = activeNodes.has(i) ? 'rgba(6, 182, 164, 0.3)' : BORDER;
        ctx.lineWidth = 1.5;
        ctx.stroke();
      }

      // "Agent" label on the left
      ctx.font = '11px Inter, sans-serif';
      ctx.fillStyle = TEXT_SEC;
      ctx.textAlign = 'right';
      ctx.fillText('Agent', nodes[0].x - nodeW / 2 - 14, nodes[0].y + 4);

      // "Result" label on the right
      ctx.textAlign = 'left';
      ctx.fillText('Result', nodes[nodes.length - 1].x + nodeW / 2 + 14, nodes[nodes.length - 1].y + 4);

      // Draw nodes
      nodes.forEach((node, i) => {
        const isActive = activeNodes.has(i) || activeNodes.has(i - 1);

        // Node glow
        if (isActive) {
          ctx.shadowColor = TEAL;
          ctx.shadowBlur = 12;
        }

        // Rounded rect
        const rx = 6;
        const x = node.x - nodeW / 2;
        const y = node.y - nodeH / 2;
        ctx.beginPath();
        ctx.roundRect(x, y, nodeW, nodeH, rx);
        ctx.fillStyle = isActive ? 'rgba(6, 182, 164, 0.08)' : SURFACE1;
        ctx.fill();
        ctx.strokeStyle = isActive ? 'rgba(6, 182, 164, 0.5)' : BORDER;
        ctx.lineWidth = 1;
        ctx.stroke();
        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 0;

        // Label
        ctx.font = '10px Inter, sans-serif';
        ctx.fillStyle = isActive ? TEXT_PRI : TEXT_SEC;
        ctx.textAlign = 'center';
        ctx.fillText(PIPELINE_STEPS[i], node.x, node.y + 4);
      });

      // Spawn logic: next dot only after the latest active dot passes 80%
      const activeDots = dots.filter(d => !d.dead);
      const lastDot = activeDots[activeDots.length - 1];
      if (!lastDot || lastDot.progress >= 0.8 || (lastDot.type === 'deny' && lastDot.denyForkProgress > 0.5)) {
        spawnDot();
      }

      // Update and draw dots
      for (let i = dots.length - 1; i >= 0; i--) {
        const dot = dots[i];
        if (dot.dead) { dots.splice(i, 1); continue; }

        // Deny at policy node (~38%)
        if (dot.type === 'deny' && dot.progress >= 0.38 && dot.denyForkProgress < 1) {
          dot.color = RED;
          dot.progress = 0.38; // freeze at policy
          dot.denyForkProgress += 0.02;

          // Draw deny fork downward
          const policyPos = getPathPosition(0.38, nodes);
          const forkY = policyPos.y + dot.denyForkProgress * 60;

          ctx.beginPath();
          ctx.moveTo(policyPos.x, policyPos.y);
          ctx.lineTo(policyPos.x, forkY);
          ctx.strokeStyle = `rgba(248, 113, 113, ${1 - dot.denyForkProgress})`;
          ctx.lineWidth = 2;
          ctx.stroke();

          // Red dot at fork end
          ctx.beginPath();
          ctx.arc(policyPos.x, forkY, 4, 0, Math.PI * 2);
          ctx.fillStyle = RED;
          ctx.shadowColor = RED;
          ctx.shadowBlur = 10;
          ctx.fill();
          ctx.shadowColor = 'transparent';
          ctx.shadowBlur = 0;

          // "DENIED" label
          if (dot.denyForkProgress > 0.3) {
            ctx.font = 'bold 9px Inter, sans-serif';
            ctx.fillStyle = `rgba(248, 113, 113, ${Math.min(1, (dot.denyForkProgress - 0.3) * 3)})`;
            ctx.textAlign = 'center';
            ctx.fillText('DENIED', policyPos.x, forkY + 16);
          }

          if (dot.denyForkProgress >= 1) dot.dead = true;
          continue;
        }

        // Approval pause at policy node
        if (dot.type === 'approval' && dot.pauseAt !== undefined && dot.progress >= dot.pauseAt && dot.pauseRemaining > 0) {
          dot.color = AMBER;
          dot.progress = dot.pauseAt;
          dot.pauseRemaining--;

          // Draw pulsing glow
          const policyPos = getPathPosition(dot.pauseAt, nodes);
          const pulsePhase = Math.sin(Date.now() / 200) * 0.5 + 0.5;
          ctx.beginPath();
          ctx.arc(policyPos.x, policyPos.y, 8 + pulsePhase * 6, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(251, 191, 36, ${0.1 + pulsePhase * 0.15})`;
          ctx.fill();

          // "APPROVAL" label
          ctx.font = 'bold 9px Inter, sans-serif';
          ctx.fillStyle = `rgba(251, 191, 36, ${0.6 + pulsePhase * 0.4})`;
          ctx.textAlign = 'center';
          ctx.fillText('APPROVAL', policyPos.x, policyPos.y - 24);

          if (dot.pauseRemaining <= 0) {
            dot.color = TEAL;
            dot.pauseAt = undefined;
          }
        } else {
          dot.progress += dot.speed;
        }

        if (dot.progress >= 1) {
          dot.dead = true;
          continue;
        }

        // Draw the traveling dot
        const pos = getPathPosition(dot.progress, nodes);
        ctx.beginPath();
        ctx.arc(pos.x, pos.y, 4, 0, Math.PI * 2);
        ctx.fillStyle = dot.color;
        ctx.shadowColor = dot.color;
        ctx.shadowBlur = 14;
        ctx.fill();
        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 0;

        // Trail
        for (let t = 1; t <= 5; t++) {
          const trailP = Math.max(0, dot.progress - t * 0.008);
          const trailPos = getPathPosition(trailP, nodes);
          ctx.beginPath();
          ctx.arc(trailPos.x, trailPos.y, 3 - t * 0.4, 0, Math.PI * 2);
          ctx.fillStyle = dot.color.replace(')', `, ${0.4 - t * 0.07})`).replace('rgb', 'rgba').replace('##', '#');
          // Simplified alpha trail
          ctx.globalAlpha = 0.4 - t * 0.07;
          ctx.fillStyle = dot.color;
          ctx.fill();
          ctx.globalAlpha = 1;
        }
      }

      animFrameRef.current = requestAnimationFrame(draw);
    }

    animFrameRef.current = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(animFrameRef.current);
      window.removeEventListener('resize', resize);
    };
  }, [isVisible]);

  return (
    <div ref={containerRef} className="relative w-full">
      <canvas
        ref={canvasRef}
        className="w-full"
        style={{ height: 180 }}
      />

      {/* Live counters */}
      <div className="mt-8 flex items-center justify-center gap-10 text-sm font-mono">
        <CounterStat value={processed} label="processed" />
        <div className="h-4 w-px bg-[var(--border)]" />
        <CounterStat value={blocked} label="blocked" color="text-[var(--color-error)]" />
        <div className="h-4 w-px bg-[var(--border)]" />
        <CounterStat value={pending} label="pending approval" color="text-[var(--color-warning)]" />
      </div>
    </div>
  );
}

function CounterStat({ value, label, color = 'text-accent-teal' }: { value: number; label: string; color?: string }) {
  return (
    <span className="text-text-secondary">
      <span className={`${color} font-semibold tabular-nums`}>
        {value.toLocaleString()}
      </span>
      {' '}{label}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/*  Social Proof Live Feed                                             */
/* ------------------------------------------------------------------ */

function LiveFeed() {
  const [rows, setRows] = useState(FEED_ENTRIES.slice(0, 6));
  const feedIndex = useRef(6);

  useEffect(() => {
    const interval = setInterval(() => {
      const next = FEED_ENTRIES[feedIndex.current % FEED_ENTRIES.length];
      feedIndex.current++;
      // Update "ago" time to feel live
      const entry = { ...next, ago: `${Math.floor(Math.random() * 4) + 1}s` };
      setRows(prev => [entry, ...prev.slice(0, 5)]);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="rounded-xl border border-[var(--border)]" style={{ backgroundColor: 'var(--surface-1)' }}>
      {/* Terminal title bar */}
      <div className="flex items-center gap-2 border-b border-[var(--border)] px-4 py-2.5">
        <div className="flex items-center gap-1.5">
          <div className="h-2.5 w-2.5 rounded-full bg-[var(--color-error)] opacity-60" />
          <div className="h-2.5 w-2.5 rounded-full bg-[var(--color-warning)] opacity-60" />
          <div className="h-2.5 w-2.5 rounded-full bg-[var(--color-success)] opacity-60" />
        </div>
        <span className="ml-2 text-xs text-text-tertiary font-mono">palaryn firewall — live feed</span>
        <div className="ml-auto flex items-center gap-1.5">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent-teal opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-accent-teal" />
          </span>
          <span className="text-xs text-accent-teal font-mono">streaming</span>
        </div>
      </div>

      {/* Scrollable table area */}
      <div className="overflow-x-auto">
        <div style={{ minWidth: 600 }}>
          {/* Header row */}
          <div
            className="grid gap-3 px-4 py-2.5 text-[10px] font-mono uppercase tracking-wider text-text-tertiary border-b border-[var(--border-subtle)]"
            style={{ gridTemplateColumns: '48px 1fr 1fr 1fr 80px 56px' }}
          >
            <span>time</span>
            <span>actor</span>
            <span>tool</span>
            <span>target</span>
            <span>decision</span>
            <span className="text-right">latency</span>
          </div>

          {/* Feed rows */}
          <div className="divide-y divide-[var(--border-subtle)]">
            {rows.map((row, i) => (
              <motion.div
                key={`${row.actor}-${row.target}-${i}`}
                initial={i === 0 ? { opacity: 0, y: -10 } : false}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="grid gap-3 px-4 py-2.5 text-xs font-mono"
                style={{ gridTemplateColumns: '48px 1fr 1fr 1fr 80px 56px' }}
              >
                <span className="text-text-tertiary">{row.ago}</span>
                <span className="text-text-secondary truncate">{row.actor}</span>
                <span className="text-text-primary">{row.tool}</span>
                <span className="text-text-secondary truncate">{row.target}</span>
                <span className={`font-semibold ${STATUS_COLOR[row.status] || 'text-text-secondary'}`}>
                  {row.status}
                </span>
                <span className="text-text-tertiary text-right">{row.ms === '--' ? '--' : `${row.ms}ms`}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Section Wrapper with Scroll Animation                              */
/* ------------------------------------------------------------------ */

function AnimatedSection({ children, className = '', delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, amount: 0.15 });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.6, delay, ease: [0.25, 0.46, 0.45, 0.94] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/*  Logo SVG                                                           */
/* ------------------------------------------------------------------ */

function LogoIcon({ size = 28 }: { size?: number }) {
  return (
    <img src="/logo.png" alt="Palaryn" width={size} height={size} />
  );
}

/* ------------------------------------------------------------------ */
/*  Contact Email Box                                                   */
/* ------------------------------------------------------------------ */

function ContactEmailBox() {
  const [copied, setCopied] = useState(false);
  const email = 'support@palaryn.com';

  const handleCopy = () => {
    navigator.clipboard.writeText(email).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="mt-8 inline-flex items-center gap-3 rounded-xl border border-[var(--border)] bg-[var(--surface-1)] px-6 py-4">
      <span className="font-mono text-base text-text-primary select-all">{email}</span>
      <button
        onClick={handleCopy}
        className="flex items-center gap-1.5 rounded-lg border border-[var(--border)] px-3 py-1.5 text-xs font-medium text-text-secondary transition-all hover:border-[var(--text-tertiary)] hover:text-text-primary"
      >
        {copied ? (
          <>
            <Check size={14} weight="bold" className="text-accent-teal" />
            <span className="text-accent-teal">Copied</span>
          </>
        ) : (
          <>
            <Copy size={14} />
            <span>Copy</span>
          </>
        )}
      </button>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main Landing Component                                             */
/* ------------------------------------------------------------------ */

export function Landing() {
  return (
    <div className="min-h-screen bg-surface-0">
      {/* ---- Navigation ---- */}
      <nav className="sticky top-0 z-50 border-b border-[var(--border)] backdrop-blur-xl" style={{ backgroundColor: 'rgba(11, 14, 19, 0.8)' }}>
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-3.5">
          <div className="flex items-center gap-2.5">
            <LogoIcon />
            <span className="text-lg font-semibold text-text-primary tracking-tight">Palaryn</span>
          </div>
          <div className="flex items-center gap-6">
            <Link
              to="/docs"
              className="text-sm text-text-secondary hover:text-text-primary transition-colors"
            >
              Docs
            </Link>
            <a
              href="#contact"
              className="text-sm text-text-secondary hover:text-text-primary transition-colors"
            >
              Contact
            </a>
            <Link
              to="/login"
              className="rounded-lg px-4 py-1.5 text-sm font-medium text-white transition-all hover:opacity-90"
              style={{ background: 'var(--gradient-accent)' }}
            >
              Sign in
            </Link>
          </div>
        </div>
      </nav>

      {/* ---- Hero ---- */}
      <section className="relative overflow-hidden">
        {/* Background radial glow */}
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background: 'radial-gradient(ellipse 60% 50% at 50% 0%, rgba(6, 182, 164, 0.08) 0%, transparent 70%), radial-gradient(ellipse 40% 40% at 60% 10%, rgba(139, 92, 246, 0.06) 0%, transparent 60%)',
          }}
        />

        <div className="relative mx-auto max-w-4xl px-6 pt-28 pb-20 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-5xl font-bold tracking-tight text-text-primary sm:text-6xl lg:text-7xl leading-[1.1]">
              The AI Agent Firewall
            </h1>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
          >
            <p className="mt-2 text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl">
              <span className="text-gradient">that controls everything.</span>
            </p>
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mx-auto mt-8 max-w-2xl text-lg text-text-secondary leading-relaxed"
          >
            Every tool call your agents make goes through one enforcement point.
            Policy rules, DLP scanning, budget limits, and immutable audit
            trails — for Claude, OpenAI, LangGraph, n8n, or any orchestrator.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.45 }}
            className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row"
          >
            <a
              href="#contact"
              className="rounded-lg px-7 py-3 text-sm font-semibold text-white shadow-lg transition-all hover:opacity-90 hover:shadow-xl"
              style={{ background: 'var(--gradient-accent)' }}
            >
              Contact Us to Get Started
            </a>
          </motion.div>
        </div>
      </section>

      {/* ---- Pipeline Animation ---- */}
      <AnimatedSection className="mx-auto max-w-6xl px-6 pb-24">
        <div className="rounded-2xl border border-[var(--border)] bg-[var(--surface-1)] p-6 sm:p-10">
          <p className="mb-6 text-center text-xs font-mono uppercase tracking-widest text-text-tertiary">
            Enforcement pipeline — live
          </p>
          <PipelineVisualization />
        </div>
      </AnimatedSection>

      {/* ---- Features Grid ---- */}
      <section className="py-24">
        <div className="mx-auto max-w-7xl px-6">
          <AnimatedSection>
            <h2 className="text-center text-3xl font-bold text-text-primary">
              Everything you need to secure agent I/O
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-center text-text-secondary">
              Palaryn provides comprehensive security, cost management, and
              governance for all your AI agent tool calls.
            </p>
          </AnimatedSection>

          <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {[
              {
                icon: Shield,
                title: 'Security & DLP',
                description: 'Policy enforcement with domain allowlists, secrets and PII detection, automatic redaction, and human-in-the-loop approval workflows for sensitive operations.',
                iconColor: 'text-accent-teal',
                iconBg: 'bg-[var(--color-teal-muted)]',
              },
              {
                icon: CurrencyDollar,
                title: 'Cost Control',
                description: 'Per-task, per-user, and per-organization budgets with step limits, sliding-window rate limiting, response caching, and request deduplication.',
                iconColor: 'text-accent-violet',
                iconBg: 'bg-[var(--color-violet-muted)]',
              },
              {
                icon: Eye,
                title: 'Integrates with Your Stack',
                description: 'Rich OpenTelemetry traces with GenAI semantic conventions pipe enforcement data to Datadog, Grafana, or Elastic. Prometheus metrics, webhook alerts, and immutable audit logs included.',
                iconColor: 'text-accent-magenta',
                iconBg: 'bg-[rgba(236,72,153,0.12)]',
              },
            ].map((feature, i) => (
              <AnimatedSection key={feature.title} delay={i * 0.1}>
                <div className="h-full rounded-xl border border-[var(--border)] bg-[var(--surface-1)] p-7 transition-colors hover:border-[var(--text-tertiary)]">
                  <div className={`flex h-11 w-11 items-center justify-center rounded-lg ${feature.iconBg}`}>
                    <feature.icon size={22} weight="duotone" className={feature.iconColor} />
                  </div>
                  <h3 className="mt-5 text-base font-semibold text-text-primary">
                    {feature.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-text-secondary">
                    {feature.description}
                  </p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* ---- Social Proof Live Feed ---- */}
      <section className="py-24">
        <div className="mx-auto max-w-5xl px-6">
          <AnimatedSection>
            <h2 className="text-center text-2xl font-bold text-text-primary">
              Watch requests flow in real time
            </h2>
            <p className="mx-auto mt-3 max-w-xl text-center text-sm text-text-secondary">
              Every tool call from every agent, inspected, policy-checked, and logged.
            </p>
          </AnimatedSection>
          <AnimatedSection delay={0.15} className="mt-10">
            <LiveFeed />
          </AnimatedSection>
        </div>
      </section>

      {/* ---- Contact ---- */}
      <section id="contact" className="py-24">
        <div className="mx-auto max-w-lg px-6 text-center">
          <AnimatedSection>
            <h2 className="text-3xl font-bold text-text-primary">Get in Touch</h2>
            <p className="mt-4 text-text-secondary">
              Interested in Palaryn? Reach out and we'll get you set up.
            </p>
          </AnimatedSection>
          <AnimatedSection delay={0.15}>
            <ContactEmailBox />
          </AnimatedSection>
        </div>
      </section>

      {/* ---- Footer ---- */}
      <footer className="border-t border-[var(--border)] bg-[var(--surface-1)]">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-6 px-6 py-10 sm:flex-row">
          <div className="flex items-center gap-2.5">
            <LogoIcon size={22} />
            <span className="text-sm font-medium text-text-primary">Palaryn</span>
            <span className="text-sm text-text-tertiary">— The AI Agent Firewall</span>
          </div>
          <div className="flex items-center gap-6">
            <Link to="/docs" className="text-sm text-text-secondary hover:text-text-primary transition-colors">
              Docs
            </Link>
            <a href="#contact" className="text-sm text-text-secondary hover:text-text-primary transition-colors">
              Contact
            </a>
            <Link to="/terms" className="text-sm text-text-secondary hover:text-text-primary transition-colors">
              Terms
            </Link>
            <Link to="/privacy" className="text-sm text-text-secondary hover:text-text-primary transition-colors">
              Privacy
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
